import { COLOR_GRAY } from "../../constants"
import type { ReportedHourViewModel } from "../../hour-reports/api"
import { convertDecimalHoursToHoursAndMinutes } from "../../hour-reports/utils"
import { escapeHtml } from "../utils"
import { TreeGridReportedHour } from "./types"
import type { HourReportingWorkflowSettings } from "../../hour-reports/hooks/use-hour-reporting-workflow-settings"
import type { HourReportStatus } from "../../hour-reports/api"

export const makeTreeGridReportedHourRows = (props: MakeRowsProps): TreeGridReportedHour[] => {
  return props.reports.map((report) => makeTreeGridReportedHourRow({ report, ...props }))
}

const makeTreeGridReportedHourRow = ({
  report,
  dateFormat,
  workflowSettings,
  statusLabels,
  notSelected,
}: MakeTreeGridReportedHourRowProps): TreeGridReportedHour => {
  const project = report.project?.title || notSelected
  const customer = report.project?.customer || notSelected
  const projectOrTask = makeProjectOrTask(report, notSelected)
  const durationInHoursAndMinutes = convertDecimalHoursToHoursAndMinutes(report.hours)

  return {
    id: report.id,
    date: report.date ? new Date(report.date).getTime() : 0,
    dateFormat,
    project,
    projectOrTask: projectOrTask.html,
    projectOrTaskSearchValue: projectOrTask.searchValue,
    projectOrTaskExportValue: projectOrTask.exportValue,
    customer,
    description: report.description || "-",
    started: report.startTime || "-",
    finished: report.endTime || "-",
    hours: `${durationInHoursAndMinutes.hours}h ${durationInHoursAndMinutes.minutes}m`,
    hoursClass: "", // Overrides the classname set by hoursColumn definition
    hoursFormula: "", // Overrides the formula set by hoursColumn definition, to show the value in field `hours`
    status: formatReportedHourStatus(report.status, statusLabels, workflowSettings),
    durationInDecimalHours: report.hours,
    worker: report.memberFullName || report.reporterUserFullName || "-",
    Height: 54,
    MaxHeight: 72,
    CanEdit: 0,
  }
}

const makeProjectOrTask = (report: ReportedHourViewModel, notSelected: string) => {
  if (report.task) return makeHourReportTask(report, notSelected)
  if (report.project) return makeHourReportProject(report, notSelected)

  return makeNotSelectedProjectOrTask(notSelected)
}

const makeHourReportTask = (report: ReportedHourViewModel, notSelected: string) => {
  const title = report.task?.title || notSelected
  const pathParts = getHourReportTaskPathParts(report)

  return {
    html: makeHourReportTaskHtml({ pathParts, title }),
    searchValue: [...pathParts, title].filter(Boolean).join(" "),
    exportValue: [...pathParts, title].filter(Boolean).join(" / "),
  }
}

const makeHourReportProject = (report: ReportedHourViewModel, notSelected: string) => {
  const title = report.project ? `${report.project.projectNumber} ${report.project.title}` : notSelected

  return {
    html: makeHourReportProjectHtml(title),
    searchValue: title,
    exportValue: title,
  }
}

const makeNotSelectedProjectOrTask = (title: string) => {
  return {
    html: makeHourReportProjectHtml(title),
    searchValue: title,
    exportValue: title,
  }
}

const getHourReportTaskPathParts = (report: ReportedHourViewModel) => {
  if (!report.project || !report.task?.taskNumber) return []

  return [
    report.task.taskNumber,
    report.project.title,
    ...(report.task.ancestors || []).map((ancestor) => ancestor.title),
  ]
}

const makeHourReportTaskHtml = ({ pathParts, title }: { pathParts: string[]; title: string }) => {
  const pathHtml = pathParts.length
    ? `<div style="line-height:1.3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${makeHourReportTaskPathHtml(
        pathParts
      )}</div>`
    : ""

  return `${pathHtml}${makeHourReportTaskTitleHtml(title)}`
}

const makeHourReportTaskTitleHtml = (title: string) => {
  return `<div style="line-height:1.4;">${escapeHtml(title)}</div>`
}

const makeHourReportProjectHtml = (title: string) => {
  return `<div style="line-height:1.4;">${escapeHtml(title)}</div>`
}

const makeHourReportTaskPathHtml = (pathParts: string[]) => {
  const [projectOrTaskNumber, projectTitle, ...ancestorTitles] = pathParts
  const spanStyles = `color:${COLOR_GRAY};cursor:pointer;font-size:0.625rem;font-weight:400;overflow:hidden;`
  const numberHtml = `<span style="${spanStyles}">${escapeHtml(projectOrTaskNumber)}</span>`
  const hasAncestorTasks = ancestorTitles.length > 0
  const projectDivider = hasAncestorTasks ? " / " : ""
  const projectHtml = `<span style="${spanStyles}">&nbsp;${escapeHtml(projectTitle)} ${projectDivider}</span>`
  const ancestorHtml = ancestorTitles
    .map((ancestorTitle, index) => {
      const isLast = index === ancestorTitles.length - 1
      const divider = isLast ? "" : " / "

      return `<span style="${spanStyles}">${escapeHtml(ancestorTitle)} ${divider}</span>`
    })
    .join("")

  return `${numberHtml}${projectHtml}${ancestorHtml}`
}

export const formatReportedHourStatus = (
  status: HourReportStatus,
  labels: ReportedHourStatusLabels,
  workflowSettings?: HourReportingWorkflowSettings
) => {
  if (workflowSettings?.isReportingOnlyWorkflow || status === "open") return labels.reported
  if (status === "submitted") {
    return workflowSettings?.supportsChecking === false ? labels.readyForApproval : labels.readyForCheck
  }
  if (status === "checked") return labels.readyForApproval
  return labels.approved
}

type MakeTreeGridReportedHourRowProps = {
  report: ReportedHourViewModel
  dateFormat: string
  workflowSettings?: HourReportingWorkflowSettings
  statusLabels: ReportedHourStatusLabels
  notSelected: string
}

type MakeRowsProps = {
  reports: ReportedHourViewModel[]
  dateFormat: string
  workflowSettings?: HourReportingWorkflowSettings
  statusLabels: ReportedHourStatusLabels
  notSelected: string
}

export type ReportedHourStatusLabels = {
  reported: string
  readyForCheck: string
  readyForApproval: string
  approved: string
}
